import React, { useEffect, useMemo, useState } from "react";
import { CloudSun, RefreshCw, Info } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import WeatherBackfillDialog from "../pages/projectDetail/WeatherBackfillDialog";
import { fmtDateGB } from "../lib/format";

/**
 * GitHub-style weather-coverage calendar heatmap.
 *
 * Rows = day-of-week (Mon → Sun), Columns = weeks. Each cell is coloured
 * by hourly-GHI coverage for that day:
 *   red    → 0 hours
 *   amber  → 1-19 hours (partial)
 *   green  → 20-24 hours (complete)
 *
 * Hover a cell for a tooltip showing the date + record count.
 * **Click** a partial/missing cell to open the weather backfill dialog
 * with a ±3-day range around that date pre-filled — makes the calendar
 * a one-click remediation tool, not just a diagnostic.
 * Powered by `GET /api/monitoring/{pid}/weather-coverage?lookback_days=N`.
 */
export default function WeatherCoverageHeatmap({ projectId, defaultLookback = 365 }) {
  const [lookback, setLookback] = useState(defaultLookback);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tip, setTip] = useState(null);      // { x, y, date, hours }
  const [backfillPrefill, setBackfillPrefill] = useState(null); // { start_date, end_date }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/monitoring/${projectId}/weather-coverage?lookback_days=${lookback}`,
      );
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load coverage");
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [projectId, lookback]);

  // Bucket days into weeks (columns). Week columns are Mon-anchored.
  const weeks = useMemo(() => {
    if (!data?.days) return [];
    // Pad leading days so the first column starts on Monday.
    const first = new Date(data.days[0].date + "T00:00:00Z");
    const firstDow = (first.getUTCDay() + 6) % 7; // 0=Mon..6=Sun
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (const d of data.days) cells.push(d);
    const cols = [];
    for (let i = 0; i < cells.length; i += 7) {
      cols.push(cells.slice(i, i + 7));
    }
    return cols;
  }, [data]);

  // Month labels — first week where the month changes gets a label.
  const monthLabels = useMemo(() => {
    if (!weeks.length) return [];
    const labels = [];
    let lastMonth = -1;
    weeks.forEach((col, i) => {
      const first = col.find(Boolean);
      if (!first) return labels.push({ i, text: "" });
      const m = new Date(first.date + "T00:00:00Z").getUTCMonth();
      if (m !== lastMonth) {
        labels.push({ i, text: new Date(first.date + "T00:00:00Z")
          .toLocaleString(undefined, { month: "short", timeZone: "UTC" }) });
        lastMonth = m;
      } else {
        labels.push({ i, text: "" });
      }
    });
    return labels;
  }, [weeks]);

  const cellClass = (hours) => {
    if (hours === undefined || hours === null) return "bg-transparent";
    if (hours >= 20) return "bg-emerald-500";
    if (hours >= 12) return "bg-emerald-300";
    if (hours >= 6)  return "bg-amber-300";
    if (hours >= 1)  return "bg-amber-500";
    return "bg-red-400";
  };

  const handleCellClick = (d) => {
    if (!d) return;
    // Only offer backfill for gaps — no point re-running on already-complete days
    if (d.hours >= 20) {
      toast.info(`${fmtDateGB(d.date)} already has ${d.hours} / 24 records`);
      return;
    }
    // Pre-fill ±3-day window around the missing day so the operator can
    // sweep a small band with one action.
    const base = new Date(d.date + "T00:00:00Z");
    const s = new Date(base.getTime() - 3 * 86400000).toISOString().slice(0, 10);
    const e = new Date(base.getTime() + 3 * 86400000).toISOString().slice(0, 10);
    const todayIso = new Date().toISOString().slice(0, 10);
    setBackfillPrefill({
      start_date: s,
      end_date: e > todayIso ? todayIso : e,
      trigger_date: d.date,
    });
  };

  return (
    <section className="swiss-card-flat p-6 relative" data-testid="weather-coverage-heatmap">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="label flex items-center gap-2"><CloudSun size={11} /> Weather-coverage calendar</div>
          <div className="font-display text-lg font-semibold mt-1">
            Hourly GHI records per day
          </div>
          {data && (
            <div className="text-xs muted mt-1 font-mono">
              {data.total_hours_have.toLocaleString()} / {data.total_hours_expected.toLocaleString()} hourly records
              &nbsp;·&nbsp; <span className="font-bold">{data.coverage_pct}%</span> covered
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select value={lookback}
                  onChange={(e) => setLookback(Number(e.target.value))}
                  className="input-swiss text-xs py-1"
                  data-testid="weather-coverage-lookback">
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 6 months</option>
            <option value={365}>Last year</option>
            <option value={730}>Last 2 years</option>
            <option value={1095}>Last 3 years</option>
            <option value={1826}>Last 5 years</option>
          </select>
          <button className="btn-ghost text-[10px]" onClick={load} disabled={loading}
                  data-testid="weather-coverage-refresh">
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
      </div>

      {loading && !data && <div className="text-xs muted">Loading…</div>}

      {data && weeks.length > 0 && (
        <div className="overflow-x-auto pb-2">
          {/* Month labels row */}
          <div className="flex gap-[3px] ml-8 mb-1">
            {monthLabels.map((m) => (
              <div key={m.i}
                   className="text-[9px] muted font-mono uppercase tracking-widest"
                   style={{ width: 12, minWidth: 12 }}>
                {m.text}
              </div>
            ))}
          </div>

          <div className="flex">
            {/* Day-of-week labels */}
            <div className="flex flex-col gap-[3px] mr-2 pt-[2px] text-[9px] muted font-mono uppercase">
              {["Mon", "", "Wed", "", "Fri", "", "Sun"].map((d, i) => (
                <div key={i} style={{ height: 12, lineHeight: "12px" }}>{d}</div>
              ))}
            </div>
            {/* Grid */}
            <div className="flex gap-[3px]" data-testid="weather-coverage-grid">
              {weeks.map((col, ci) => (
                <div key={ci} className="flex flex-col gap-[3px]">
                  {col.map((d, ri) => (
                    <div
                      key={ri}
                      className={`w-3 h-3 ${d ? cellClass(d.hours) : "bg-transparent"} rounded-[2px] ${d ? "cursor-pointer" : "cursor-default"} transition-transform hover:scale-125`}
                      onClick={() => handleCellClick(d)}
                      onMouseEnter={(e) => {
                        if (!d) return;
                        const rect = e.currentTarget.getBoundingClientRect();
                        setTip({
                          x: rect.left + rect.width / 2,
                          y: rect.top - 6,
                          date: d.date,
                          hours: d.hours,
                        });
                      }}
                      onMouseLeave={() => setTip(null)}
                      data-testid={d ? `weather-cell-${fmtDateGB(d.date)}` : undefined}
                      title={d ? (d.hours >= 20 ? `${fmtDateGB(d.date)} · ${d.hours}/24 hrs (complete)` : `${fmtDateGB(d.date)} · ${d.hours}/24 hrs — click to backfill`) : undefined}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Legend */}
          <div className="flex items-center gap-3 mt-4 text-[10px] muted font-mono uppercase tracking-widest">
            <span>Less</span>
            <div className="w-3 h-3 bg-red-400 rounded-[2px]" title="0 hrs" />
            <div className="w-3 h-3 bg-amber-500 rounded-[2px]" title="1-5 hrs" />
            <div className="w-3 h-3 bg-amber-300 rounded-[2px]" title="6-11 hrs" />
            <div className="w-3 h-3 bg-emerald-300 rounded-[2px]" title="12-19 hrs" />
            <div className="w-3 h-3 bg-emerald-500 rounded-[2px]" title="20-24 hrs" />
            <span>More</span>
            <span className="ml-auto flex items-center gap-1">
              <Info size={10} /> Click a red / amber cell to backfill weather around that date.
            </span>
          </div>
        </div>
      )}

      {/* Absolute tooltip (fixed to viewport) */}
      {tip && (
        <div className="fixed z-50 pointer-events-none px-2 py-1 bg-black/85 text-white text-[10px] font-mono rounded"
             style={{ left: tip.x, top: tip.y, transform: "translate(-50%, -100%)" }}
             data-testid="weather-coverage-tooltip">
          {tip.date} · <strong>{tip.hours}</strong> / 24 hrs
          {tip.hours < 20 && <span className="ml-1 opacity-80">· click to backfill</span>}
        </div>
      )}

      {backfillPrefill && (
        <WeatherBackfillDialog
          projectId={projectId}
          initialStartDate={backfillPrefill.start_date}
          initialEndDate={backfillPrefill.end_date}
          onClose={() => setBackfillPrefill(null)}
          onDone={() => { load(); }}
        />
      )}
    </section>
  );
}
